/**
 * Which stored onboarding-answer keys hold a FLOW TOOL's generated output
 * rather than something the member answered.
 *
 * The onboarding flow runs tools that write into the same state the member's
 * own answers live in, so the persisted `answers` blob mixes the two. Telling
 * them apart is not a property of any consumer — it is a property of the flow
 * definition, which declares every tool's write target explicitly. This module
 * is where that fact lives, in the identifier vocabulary both sides can reach:
 * `@galapagos/onboarding-flow` authors its tool declarations FROM these
 * constants, and a drift guard in that package fails when the two disagree in
 * either direction.
 *
 * The dependency runs that way round on purpose. `@galapagos/onboarding-flow`
 * depends on `@galapagos/design-system`, so this package cannot import it
 * (ADR-001 Rule 8, and it would be a cycle). Declaring the registry here and
 * consuming it there inverts the dependency without either side holding a
 * second copy of the list.
 *
 * Identifiers only — no display copy. This package is inside ADR-017's
 * `applies_to`, which is precisely why no wording lives here: a user-facing
 * string authored in this package would have to go through the canonical i18n
 * catalog, and the snapshot generator mirrors that catalog into every client
 * bundle with no per-namespace filtering. What these keys are CALLED is decided
 * by whichever surface renders them.
 */

/**
 * The flow-state root the onboarding GenUI document addresses its state under
 * (ADR-046 Rule 13). A persisted key may carry it or not, depending on whether
 * the writer relativised the pointer at the `save_flow_session` boundary —
 * which is why `readAnswer` tolerates four key shapes.
 */
export const FLOW_STATE_ROOT_PREFIX = '/screen/';

/**
 * Flow tools whose declared write targets hold generated view-model output.
 *
 * A tool is classified, not a target: `clear_state_unless_included` also
 * declares a `targetPath`, and it writes the member's OWN answer. A rule of the
 * form "every declared target is derived" would hide a real answer, which is
 * the reason this list names tools rather than being inferred from the flow's
 * targets.
 */
export const DERIVED_ANSWER_TOOLS = ['calculate_week_summary', 'derive_summary_bullets'] as const;
export type DerivedAnswerTool = (typeof DERIVED_ANSWER_TOOLS)[number];

/**
 * Every state path the tools above declare as a write target, as the RFC 6901
 * pointers the flow definition authors them with.
 *
 * `week_anchor_date` is included because `calculate_week_summary` declares it
 * as an `anchorPath`: the device-local date stamping the rolling day window is
 * the tool's input-stamp, not an answer to a question anybody asked.
 */

/** `calculate_week_summary`'s view-model for the week the member reported. */
export const BASELINE_WEEK_SUMMARY_POINTER = '/screen/baseline_week_summary';
/** `calculate_week_summary`'s view-model for the week the member calls typical. */
export const TYPICAL_WEEK_SUMMARY_POINTER = '/screen/typical_week_summary';
/** The device-local date both summaries stamp to anchor the rolling day window. */
export const WEEK_ANCHOR_DATE_POINTER = '/screen/week_anchor_date';
/** `derive_summary_bullets`' generated focus sentence. */
export const SUMMARY_BULLETS_FOCUS_POINTER = '/screen/summary_bullets/focus';
/** `derive_summary_bullets`' generated motivation sentence. */
export const SUMMARY_BULLETS_MOTIVATION_POINTER = '/screen/summary_bullets/motivation';

export const DERIVED_ANSWER_TARGET_POINTERS = [
  BASELINE_WEEK_SUMMARY_POINTER,
  TYPICAL_WEEK_SUMMARY_POINTER,
  WEEK_ANCHOR_DATE_POINTER,
  SUMMARY_BULLETS_FOCUS_POINTER,
  SUMMARY_BULLETS_MOTIVATION_POINTER,
] as const;
export type DerivedAnswerTargetPointer = (typeof DERIVED_ANSWER_TARGET_POINTERS)[number];

/**
 * Strip the flow-state root from a pointer, leaving the flat key persistence
 * speaks. A key that never carried the root comes back unchanged.
 */
export function relativizeAnswerPointer(pointer: string): string {
  return pointer.startsWith(FLOW_STATE_ROOT_PREFIX)
    ? pointer.slice(FLOW_STATE_ROOT_PREFIX.length)
    : pointer.replace(/^\//, '');
}

/**
 * The derived targets as the flat keys they are persisted under — DERIVED from
 * the pointers above rather than hand-listed, so the two cannot fall out of
 * step when a pointer is added.
 */
export const DERIVED_ANSWER_KEYS: readonly string[] =
  DERIVED_ANSWER_TARGET_POINTERS.map(relativizeAnswerPointer);

/** The first path segment of a flat answer key — the key itself when it has no path. */
function firstSegment(key: string): string {
  return key.split('/')[0] ?? key;
}

/**
 * The derived targets reduced to their first path segment — the unit the
 * comparison below is made in.
 *
 * A nested derived target reaches storage under TWO spellings: the writer that
 * relativises the pointer and merges leaves `summary_bullets` holding an
 * object, and the writer that leaves the pointer un-relativised leaves
 * `summary_bullets/focus` holding a string. Both are the same tool's output, so
 * comparing whole keys would classify one of them as a member's answer and
 * render a storage path as if it were a clinical label.
 */
const DERIVED_ANSWER_KEY_ROOTS: ReadonlySet<string> = new Set(
  DERIVED_ANSWER_KEYS.map(firstSegment),
);

/**
 * Whether a canonical (flat) answer key holds derived output.
 *
 * Compared on the first path segment of both sides, so every spelling of a
 * nested target classifies alike. The cost is that a member answer key sharing
 * a first segment with a derived target would be swallowed — which is why the
 * registry is small, tool-declared, and guarded against the flow definition
 * rather than being an open-ended prefix list.
 */
export function isDerivedAnswerKey(canonicalKey: string): boolean {
  return DERIVED_ANSWER_KEY_ROOTS.has(firstSegment(canonicalKey));
}
